import type { CopyFn } from '@packages/copy';
import type { PracticeType, UiOmniboxDestination } from '@packages/types';
import { appendLedgerParam } from '../../routes/ledger/use-bill-ledger';
import { tabsForLedger } from '../../routes/ledger/ledger-tabs';
import { visibleReportTabsForAccount } from '../../routes/ledger/report-tabs';
import {
  labelSettingsTabs,
  visibleSettingsTabs,
  type AccountTabId,
} from '../../routes/account/settings-tabs';
import { visibleNavItems } from '../layout/nav-items';
import { SUBLABEL_SEPARATOR } from './omnibox.items';

/** A place the omnibox can jump to. `key` is stable and doubles as the row id. */
export type Destination = {
  key: string;
  /** The closed category recorded when it is chosen; the label and path never leave the browser. */
  category: UiOmniboxDestination;
  label: string;
  /** Where it lives, shown under the label ("Settings · Billing"). */
  location: string;
  /** Space-separated extra words someone might type to find it. */
  keywords: string;
  /** Path with its search and hash, ready to hand to `navigate`. */
  to: string;
};

type Access = 'loading' | 'granted' | 'denied';

/** Everything that decides which destinations exist, gathered by the caller's hooks. */
export type DestinationInputs = {
  administered: boolean;
  accountLoading: boolean;
  practiceType: PracticeType | null;
  accountingAccess: Access;
  invoicingVisible: boolean | null;
  /** The account has a live business ledger; without one the accounting page only shows its setup picker. */
  hasActiveLedger: boolean;
  hasActiveTrustLedger: boolean;
  canManageHumanTeam: boolean;
};

type SettingsSection = {
  tab: AccountTabId;
  /** The element id on the settings screen the link scrolls to. */
  anchor: string;
  label: string;
  keywords: string;
  /** Hidden from members who cannot manage the team, whose screen has no such section. */
  needsTeamManagement?: boolean;
};

/** Sections that have an anchor on their settings screen. Order is the catalogue's tie-break. */
function settingsSections(copy: CopyFn): SettingsSection[] {
  const d = 'omnibox:destinations.sections';
  return [
    {
      tab: 'practice',
      anchor: 'practice-profile',
      label: copy('account:profile.heading'),
      keywords: copy(`${d}.practiceProfile.keywords`),
    },
    {
      tab: 'practice',
      anchor: 'business-details',
      label: copy(`${d}.businessInformation.label`),
      keywords: copy(`${d}.businessInformation.keywords`),
    },
    {
      tab: 'billing',
      anchor: 'billing-settings',
      label: copy(`${d}.billingSettings.label`),
      keywords: copy(`${d}.billingSettings.keywords`),
    },
    {
      tab: 'billing',
      anchor: 'due-date',
      label: copy(`${d}.dueDate.label`),
      keywords: copy(`${d}.dueDate.keywords`),
    },
    {
      tab: 'billing',
      anchor: 'matter-reference',
      label: copy('account:references.heading'),
      keywords: copy(`${d}.matterReference.keywords`),
    },
    {
      tab: 'billing',
      anchor: 'invoice-bank-accounts',
      label: copy(`${d}.invoiceBankAccounts.label`),
      keywords: copy(`${d}.invoiceBankAccounts.keywords`),
    },
    {
      tab: 'team',
      anchor: 'invite-member',
      label: copy(`${d}.inviteMembers.label`),
      keywords: copy(`${d}.inviteMembers.keywords`),
      needsTeamManagement: true,
    },
    {
      tab: 'subscription',
      anchor: 'payment-history',
      label: copy(`${d}.paymentHistory.label`),
      keywords: copy(`${d}.paymentHistory.keywords`),
    },
    {
      tab: 'profile',
      anchor: 'whatsapp-number',
      label: copy(`${d}.whatsappNumber.label`),
      keywords: copy(`${d}.whatsappNumber.keywords`),
    },
    {
      tab: 'profile',
      anchor: 'sign-in-methods',
      label: copy(`${d}.signInMethods.label`),
      keywords: copy(`${d}.signInMethods.keywords`),
    },
    {
      tab: 'notifications',
      anchor: 'notification-channels',
      label: copy(`${d}.notificationChannels.label`),
      keywords: copy(`${d}.notificationChannels.keywords`),
    },
    {
      tab: 'notifications',
      anchor: 'delivery-hours',
      label: copy(`${d}.deliveryHours.label`),
      keywords: copy(`${d}.deliveryHours.keywords`),
    },
    {
      tab: 'accounting',
      anchor: 'year-end-vat',
      label: copy(`${d}.yearEndAndVat.label`),
      keywords: copy(`${d}.yearEndAndVat.keywords`),
    },
    {
      tab: 'privacy',
      anchor: 'csv-import-export',
      label: copy(`${d}.csvImportExport.label`),
      keywords: copy(`${d}.csvImportExport.keywords`),
    },
    {
      tab: 'privacy',
      anchor: 'data-export',
      label: copy(`${d}.dataExport.label`),
      keywords: copy(`${d}.dataExport.keywords`),
    },
    {
      tab: 'privacy',
      anchor: 'delete-data',
      label: copy(`${d}.deleteData.label`),
      keywords: copy(`${d}.deleteData.keywords`),
    },
  ];
}

function joinLocation(...parts: string[]): string {
  return parts.join(SUBLABEL_SEPARATOR);
}

/**
 * The full "Go to" catalogue for one account, in tie-break order: pages,
 * settings tabs, settings sections, then accounting tabs and reports.
 *
 * Nothing here decides visibility itself. Pages, settings tabs, accounting
 * tabs and reports each ask the same pure function their own screen uses
 * (`visibleNavItems`, `visibleSettingsTabs`, `tabsForLedger`,
 * `visibleReportTabsForAccount`), so a destination is offered exactly when its
 * screen would show it. The accounting sub-destinations additionally need a
 * live ledger of the right kind, because without one the page shows its setup
 * picker instead of tabs.
 */
export function buildDestinations(inputs: DestinationInputs, copy: CopyFn): Destination[] {
  const accountingGranted = inputs.accountingAccess === 'granted';
  const settingsLocation = copy('omnibox:destinations.location.settings');
  const accountingLocation = copy('omnibox:destinations.location.accounting');
  const destinations: Destination[] = [];

  for (const item of visibleNavItems({
    accountingVisible: accountingGranted,
    administered: inputs.administered,
    accountLoading: inputs.accountLoading,
    invoicingVisible: inputs.invoicingVisible,
  })) {
    destinations.push({
      key: `page:${item.id}`,
      category: 'page',
      label: item.label,
      location: copy('omnibox:destinations.location.page'),
      keywords: copy(`omnibox:destinations.pageKeywords.${item.id}`),
      to: item.to,
    });
  }

  const settingsTabs = labelSettingsTabs(
    visibleSettingsTabs({
      administered: inputs.administered,
      accountingAccess: inputs.accountingAccess,
    }),
    {
      administered: inputs.administered,
      practiceType: inputs.practiceType,
      organisationLabel: copy('account:organisationLabel'),
    },
  );
  for (const tab of settingsTabs) {
    destinations.push({
      key: `settings:${tab.id}`,
      category: 'settings-tab',
      label: tab.label,
      location: settingsLocation,
      keywords: copy(`omnibox:destinations.settingsTabKeywords.${tab.id}`),
      to: `/settings?tab=${tab.id}`,
    });
  }

  const tabLabels = new Map<string, string>(settingsTabs.map((tab) => [tab.id, tab.label]));
  for (const section of settingsSections(copy)) {
    const tabLabel = tabLabels.get(section.tab);
    if (tabLabel === undefined) continue;
    if (section.needsTeamManagement && !inputs.canManageHumanTeam) continue;
    destinations.push({
      key: `settings:${section.tab}#${section.anchor}`,
      category: 'settings-section',
      label: section.label,
      location: joinLocation(settingsLocation, tabLabel),
      keywords: section.keywords,
      to: `/settings?tab=${section.tab}#${section.anchor}`,
    });
  }

  if (!accountingGranted) return destinations;

  if (inputs.hasActiveLedger) {
    for (const tab of tabsForLedger('business')) {
      destinations.push({
        key: `accounting:${tab.id}`,
        category: 'accounting-tab',
        label: tab.label,
        location: accountingLocation,
        keywords: copy(`omnibox:destinations.ledgerTabKeywords.${tab.id}`),
        to: `/accounting?tab=${tab.id}`,
      });
    }
    const reports = visibleReportTabsForAccount({
      ledgerKind: 'BUSINESS',
      practiceType: inputs.practiceType,
      isAccountLoading: inputs.accountLoading,
      administered: inputs.administered,
    });
    for (const report of reports) {
      destinations.push({
        key: `accounting:reports:${report.id}`,
        category: 'accounting-report',
        label: report.label,
        location: joinLocation(accountingLocation, copy('omnibox:destinations.location.reports')),
        keywords: copy(`omnibox:destinations.reportKeywords.${report.id}`),
        to: `/accounting?tab=reports&report=${report.id}`,
      });
    }
  }

  if (inputs.hasActiveTrustLedger) {
    const trustReports = visibleReportTabsForAccount({
      ledgerKind: 'TRUST',
      practiceType: inputs.practiceType,
      isAccountLoading: inputs.accountLoading,
      administered: inputs.administered,
    });
    for (const report of trustReports) {
      destinations.push({
        key: `accounting:trust-reports:${report.id}`,
        category: 'accounting-report',
        // The trust reconciliation only exists on the trust ledger; the trial balance
        // exists on both, so the trust one says which it is.
        label:
          report.id === 'trust-creditors'
            ? report.label
            : copy('omnibox:destinations.trustReport', { report: report.label.toLowerCase() }),
        location: joinLocation(accountingLocation, copy('omnibox:destinations.location.trustReports')),
        keywords: copy(`omnibox:destinations.reportKeywords.${report.id}`),
        to: appendLedgerParam(`/accounting?tab=reports&report=${report.id}`, 'trust'),
      });
    }
  }

  return destinations;
}
